import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  accountRequest,
  redirectTo,
  type AccountSummary,
  type PaidPlan,
} from '../../utils/accountApi';
import { formatDate, getPlanDetails } from '../../utils/plans';
import LegalLinks from '../Legal/LegalLinks';

type Action = 'change' | 'cancel' | 'resume' | 'portal';

const AccountBilling: React.FC = () => {
  const { user, refreshAccount, setAccount, signOut } = useAuth();
  const [busy, setBusy] = useState<Action | null>(null);
  const [confirming, setConfirming] = useState<Action | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    refreshAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) {
    return null;
  }

  if (user.accountType !== 'account') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-5">
        <div className="max-w-md rounded-2xl bg-white p-8 text-center shadow-xl">
          <h1 className="text-2xl font-bold text-slate-900">Plan &amp; billing</h1>
          <p className="mt-2 text-slate-600">
            Your team login includes every Pro feature. There&apos;s no
            subscription to manage.
          </p>
          <a href="/" className="mt-6 inline-block text-indigo-600">
            Back to practice
          </a>
        </div>
      </div>
    );
  }

  const account = user.account;
  const plan = account?.plan ?? 'none';
  const details = plan === 'none' ? null : getPlanDetails(plan);
  const otherPlan: PaidPlan = plan === 'pro' ? 'standard' : 'pro';
  const otherDetails = getPlanDetails(otherPlan);
  const renewal = formatDate(account?.current_period_end);

  const run = async (action: Action, path: string, body?: unknown) => {
    setBusy(action);
    setError(null);
    setMessage(null);
    try {
      const result = await accountRequest<{ account?: AccountSummary; url?: string }>(path, {
        method: 'POST',
        token: user.token,
        body,
      });
      if (result.url) {
        redirectTo(result.url);
        return;
      }
      if (result.account) {
        setAccount(result.account);
        setMessage(
          action === 'change'
            ? `You're now on ${otherDetails.name}.`
            : action === 'cancel'
              ? `Your subscription is cancelled. You keep access until ${formatDate(result.account.current_period_end)}.`
              : 'Your subscription will continue as normal.'
        );
      }
      setConfirming(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <a href="/" className="text-sm text-indigo-600">
            ← Back to practice
          </a>
          <button type="button" onClick={signOut} className="text-sm text-slate-500 hover:text-slate-700">
            Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-10">
        <h1 className="text-3xl font-bold text-slate-900">Plan &amp; billing</h1>
        <p className="mt-1 text-sm text-slate-500">{account?.email}</p>

        {error && (
          <div role="alert" className="mt-6 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}
        {message && (
          <div role="status" className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            {message}
          </div>
        )}

        {!details ? (
          <section className="mt-6 rounded-2xl bg-white p-6 shadow ring-1 ring-slate-200">
            <h2 className="m-0 text-lg font-semibold text-slate-900">No active plan</h2>
            <p className="mt-2 text-sm text-slate-600">
              Choose Standard or Pro to start practising.
            </p>
            <a
              href="/pricing"
              className="mt-4 inline-block rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-2.5 text-sm font-semibold text-white no-underline"
            >
              See plans
            </a>
          </section>
        ) : (
          <>
            <section className="mt-6 rounded-2xl bg-white p-6 shadow ring-1 ring-slate-200">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="m-0 text-lg font-semibold text-slate-900">
                  {details.name} plan
                </h2>
                <span className="text-2xl font-bold text-slate-900">
                  £{details.monthlyPrice}
                  <span className="text-sm font-normal text-slate-500">/month</span>
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-600">
                {account?.cancel_at_period_end
                  ? `Cancelled. You have access until ${renewal}, and you won't be charged again.`
                  : account?.subscription_status === 'past_due'
                    ? 'Your last payment failed. Update your payment method to keep your plan.'
                    : `Renews automatically on ${renewal}.`}
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                {account?.cancel_at_period_end ? (
                  <button
                    type="button"
                    disabled={!!busy}
                    onClick={() => run('resume', '/billing/resume')}
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  >
                    {busy === 'resume' ? 'Resuming…' : 'Resume subscription'}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={!!busy}
                    onClick={() => setConfirming('change')}
                    className="rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  >
                    {otherPlan === 'pro' ? 'Upgrade to Pro' : 'Switch to Standard'}
                  </button>
                )}
                {account?.has_billing_account && (
                  <button
                    type="button"
                    disabled={!!busy}
                    onClick={() => run('portal', '/billing/portal')}
                    className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 disabled:opacity-60"
                  >
                    {busy === 'portal' ? 'Opening…' : 'Payment method & invoices'}
                  </button>
                )}
                {!account?.cancel_at_period_end && (
                  <button
                    type="button"
                    disabled={!!busy}
                    onClick={() => setConfirming('cancel')}
                    className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 disabled:opacity-60"
                  >
                    Cancel subscription
                  </button>
                )}
              </div>

              {confirming === 'change' && (
                <div className="mt-5 rounded-xl bg-indigo-50 p-4 text-sm text-slate-700">
                  <p className="m-0">
                    {otherPlan === 'pro'
                      ? `Switch to Pro for £${otherDetails.monthlyPrice}/month. You'll be charged the difference for the rest of this billing month now.`
                      : `Switch to Standard for £${otherDetails.monthlyPrice}/month. Pro-only features stop straight away and the unused difference is credited to your next payment.`}
                  </p>
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      disabled={!!busy}
                      onClick={() => run('change', '/billing/change-plan', { plan: otherPlan })}
                      className="rounded-lg bg-indigo-600 px-4 py-2 font-semibold text-white disabled:opacity-60"
                    >
                      {busy === 'change' ? 'Switching…' : `Confirm switch to ${otherDetails.name}`}
                    </button>
                    <button type="button" onClick={() => setConfirming(null)} className="px-3 text-slate-500">
                      Not now
                    </button>
                  </div>
                </div>
              )}

              {confirming === 'cancel' && (
                <div className="mt-5 rounded-xl bg-red-50 p-4 text-sm text-slate-700">
                  <p className="m-0">
                    You won&apos;t be charged again and you&apos;ll keep access
                    until {renewal}. If you subscribed in the last 14 days you
                    can also ask for a partial refund (see our{' '}
                    <a href="/refunds" className="text-indigo-600 underline">
                      Refunds &amp; Cancellation Policy
                    </a>
                    ).
                  </p>
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      disabled={!!busy}
                      onClick={() => run('cancel', '/billing/cancel')}
                      className="rounded-lg bg-red-600 px-4 py-2 font-semibold text-white disabled:opacity-60"
                    >
                      {busy === 'cancel' ? 'Cancelling…' : 'Confirm cancellation'}
                    </button>
                    <button type="button" onClick={() => setConfirming(null)} className="px-3 text-slate-500">
                      Keep my plan
                    </button>
                  </div>
                </div>
              )}
            </section>

            <section className="mt-6 rounded-2xl bg-white p-6 shadow ring-1 ring-slate-200">
              <h2 className="m-0 text-sm font-semibold uppercase tracking-wide text-slate-500">
                Included in {details.name}
              </h2>
              <ul className="m-0 mt-3 list-none space-y-1.5 p-0 text-sm text-slate-700">
                {details.features.map((feature) => (
                  <li key={feature}>✓ {feature}</li>
                ))}
              </ul>
            </section>
          </>
        )}
      </main>

      <footer className="border-t border-slate-200 py-6 text-slate-500">
        <LegalLinks />
      </footer>
    </div>
  );
};

export default AccountBilling;
